import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import LayerDriftSeverityBadge from "@/v2-backoffice-client/layer/drift/LayerDriftSeverityBadge"
import { useLayerDriftQuery } from "@/v2-backoffice-client/layer/drift/layer.drift.query"
import type { SeverityLabel } from "@/v2-backoffice-client/layer/drift/layer.drift.utils"
import { MAP_LAYER_MONITOR_SOURCE } from "@/v2-backoffice-client/layer/layer.constants"

export type LayerDriftTabBadgeProps = ComponentProps<"span"> & {
  featureCode?: string
}

/**
 * Severity badge for the layer sheet's Drift tab trigger. Self-fetches the
 * drift lookup so the tab can carry a severity badge before it is ever
 * opened; the tab itself runs the same query, so opening it costs no extra
 * round-trip (Apollo dedupes on the cache). Renders nothing when the layer
 * has no drift.
 */
function LayerDriftTabBadge(props: LayerDriftTabBadgeProps) {
  const { featureCode, className, ...spanProps } = props

  const { data } = useLayerDriftQuery({
    skip: !featureCode,
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        source: MAP_LAYER_MONITOR_SOURCE,
        identifier: String(featureCode ?? ""),
      },
    },
  })
  const drift = data?.getLayerDriftDetail
  const hasDrift = Boolean(drift?.HasDrift || drift?.CountDrift?.HasCountDrift)

  if (!hasDrift) return null

  const severity: SeverityLabel = drift?.HasDrift
    ? (drift.HighestSeverity as SeverityLabel) || "info"
    : "warning"
  const entryCount = (drift?.Entries.length ?? 0) || 1

  return (
    <LayerDriftSeverityBadge
      severity={severity}
      className={cn("ml-1.5", className)}
      {...spanProps}
    >
      {entryCount}
    </LayerDriftSeverityBadge>
  )
}

export default LayerDriftTabBadge
